/**
 * @file LeadFormModal.tsx
 * @description The ONE lead-capture modal for the whole site. A thin, fully
 *              controlled composition of the project's shared Radix Dialog and
 *              the shared LeadForm engine — it owns no dialog markup of its own.
 *              Replaces two hand-rolled copies (LeadFormTrigger, CarCardActions)
 *              that each re-implemented an overlay and an Escape handler while
 *              providing no focus trap, no focus return, no background scroll
 *              lock and no dialog semantics.
 * @universal   knowledge/universals/components.md#leadformmodal
 * @usage <LeadFormModal open={isOpen} onOpenChange={setIsOpen} title="Оптовые условия" source="noscut-wholesale" />
 */

"use client";

import LeadForm from "@/components/LeadForm";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

interface LeadFormModalProps {
  /** Whether the dialog is open. This component is CONTROLLED — it holds no open state. */
  open: boolean;
  /** Called with the next open state. Radix calls it for Escape, overlay click and the close cross. */
  onOpenChange: (open: boolean) => void;
  /**
   * Visible dialog heading. When omitted the dialog still gets an accessible
   * name from a visually hidden title — see the @rule below.
   */
  title?: string;
  /** Human-readable Telegram detail line. Forwarded to LeadForm unchanged. */
  subject?: string;
  /** Stable kebab-case channel label. Forwarded to LeadForm unchanged — see its source-vs-subject contract. */
  source?: string;
  /** Submit button label inside the form. Forwarded to LeadForm unchanged. */
  ctaLabel?: string;
  /** Message used when the visitor submits an empty comment. Forwarded to LeadForm unchanged. */
  fallbackComment?: string;
  /** Placeholder of the form's free-text field. Forwarded to LeadForm unchanged. */
  commentPlaceholder?: string;
}

/**
 * Controlled lead-capture modal.
 *
 * Everything a dialog needs — focus trap, focus return on close, Escape,
 * overlay click, background scroll lock, `role="dialog"` and `aria-modal` —
 * comes from the shared Dialog. This component adds only the card sizing and
 * the LeadForm wiring.
 *
 * @param props see {@link LeadFormModalProps}
 */
// @rule An accessible name is MANDATORY. Radix logs a console error when a
// Dialog has no Title, so when `title` is absent we still render a DialogTitle
// and hide it with `sr-only`. Do NOT reach for @radix-ui/react-visually-hidden
// — it is not installed and must not be added for this.
// @rule `aria-describedby={undefined}` suppresses Radix's missing-Description
// warning on purpose. The form IS the description; there is no separate
// descriptive paragraph to point at, and inventing one would add read-aloud
// noise before the phone field.
// @rule Never re-add overlay markup here, and never fork DialogOverlay or
// DialogContent. Removing the two copied overlays is the entire reason this
// component exists — a third copy would undo it. If Dialog cannot express
// something a call site needs, widen THIS component's props instead.
export default function LeadFormModal({
  open,
  onOpenChange,
  title,
  subject,
  source,
  ctaLabel,
  fallbackComment,
  commentPlaceholder,
}: LeadFormModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        Class overrides resolved by `cn` = twMerge(clsx(…)), last-wins per group:
          max-w-md      beats DialogContent's max-w-lg          → max-width: 28rem
          rounded-2xl   (DialogContent has no unprefixed radius) → radius below sm: 1rem
          sm:rounded-2xl beats DialogContent's sm:rounded-lg     → radius at sm+:   1rem
        Both radius classes are required: unprefixed and sm-prefixed are
        SEPARATE twMerge groups, so passing only `rounded-2xl` would leave
        `sm:rounded-lg` in place and the corners would silently revert at the
        sm breakpoint and above.
        max-h-[90vh] + overflow-y-auto: on a small phone in landscape with the
        keyboard raised, the heading + form + consent text exceed the visible
        viewport; the hand-rolled modal had no cap and simply clipped the
        submit button with no way to reach it.
      */}
      <DialogContent
        aria-describedby={undefined}
        className="max-h-[90vh] max-w-md overflow-y-auto rounded-2xl bg-white sm:rounded-2xl"
      >
        {title ? (
          // font-normal / leading-normal / tracking-normal neutralise
          // DialogTitle's own font-semibold / leading-none / tracking-tight,
          // which live in different twMerge groups and would otherwise survive
          // and render the heading bolder and tighter than the modal being
          // replaced. Spacing below comes from DialogContent's own `gap-4`,
          // which is exactly the `mb-4` the hand-rolled heading used — adding
          // a margin here would double it.
          <DialogTitle className="font-heading text-lg font-normal leading-normal tracking-normal text-text">
            {title}
          </DialogTitle>
        ) : (
          <DialogTitle className="sr-only">Оставить заявку</DialogTitle>
        )}
        <LeadForm
          subject={subject}
          source={source}
          ctaLabel={ctaLabel}
          fallbackComment={fallbackComment}
          commentPlaceholder={commentPlaceholder}
          successMode="auto-close"
          onSuccess={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
